import { useDispatch } from 'react-redux';
import { useToast } from '@app/core';
import { useTranslation } from 'react-i18next';
import { Thunk, ThunkDispatch } from '../store';
import { FormikHelpers } from 'formik';
import { useCallback, useMemo } from 'react';
import { useTrack } from '@app/analytics';

export type ThunkCreator<Values, T = any> = (
  values: Values,
) => Thunk<T> | void | undefined | Promise<Thunk<T> | void | undefined>;

type WithDispatchOptions<Values> = {
  onSuccess?: (helpers: FormikHelpers<Values>, values: Values, res: any) => any;
  onSubmit?: (values: Values, helpers: FormikHelpers<Values>) => any;
  onError?: (
    e: Error,
    helpers: FormikHelpers<Values>,
    values: Values,
  ) =>
    | undefined
    | boolean
    | 'RETRY'
    | Promise<boolean | undefined | 'RETRY'>
    | void;
  updateValues?: boolean | ((res: any) => Values);
  noSuccessToast?: boolean;
  successToastMessage?: string;
  name?: string;
};

/**
 * @deprecated Redux is deprecated
 */
export function useFormikDispatchSubmit<Values>(
  thunkCreator: ThunkCreator<Values>,
  {
    onSuccess,
    onError,
    onSubmit,
    updateValues,
    noSuccessToast,
    name,
  }: WithDispatchOptions<Values> = {},
) {
  const dispatch = useDispatch<ThunkDispatch>();
  const toast = useToast();
  const track = useTrack();

  const call = useCallback(
    async (values: Values, helpers: FormikHelpers<Values>) => {
      if (onSubmit) {
        onSubmit(values, helpers);
      }
      const thunk = await thunkCreator(values);
      if (!thunk) {
        return;
      }
      const res = await dispatch(thunk);
      if (!noSuccessToast) {
        toast.saved();
      }
      if (onSuccess) {
        await onSuccess(helpers, values, res);
      }
      if (updateValues) {
        if (updateValues === true) {
          return helpers.setValues(res);
        }
        return helpers.setValues(updateValues(res));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      thunkCreator,
      onSuccess,
      dispatch,
      toast,
      updateValues,
      noSuccessToast,
      onSubmit,
    ],
  );

  return useCallback(
    async (values: Values, helpers: FormikHelpers<Values>) => {
      track(name ? `${name} Submitted` : 'Submitted');
      try {
        await call(values, helpers);
      } catch (e) {
        helpers.setStatus(e);
        if (onError && e instanceof Error) {
          const hideToast = await onError(e, helpers, values);
          if (hideToast === 'RETRY') {
            try {
              await call(values, helpers);
            } catch (e) {
              helpers.setStatus(e);
              toast.exception(e);
            }
            return;
          }
          if (hideToast) {
            return;
          }
        }
        toast.exception(e);
      }
      helpers.setSubmitting(false);
    },
    [call, onError, toast, track, name], // eslint-disable-line react-hooks/exhaustive-deps
  );
}

type Options<Values, T> = {
  onSuccess?: (res: T, helpers: FormikHelpers<Values>, values: Values) => any;
  onSubmit?: (values: Values, helpers: FormikHelpers<Values>) => any;
  onError?: (
    e: Error,
    helpers: FormikHelpers<Values>,
    values: Values,
  ) => undefined | boolean | Promise<boolean | undefined> | void;
  noSuccessToast?: boolean;
  successToastMessage?: string;
  name?: string;
  noSetValues?: boolean;
};

const identity = (val: any) => val;

type SubmitCb<Values> = (values: Values, helpers: FormikHelpers<Values>) => any;

export function useFormikSubmit<Values, T = Values>(
  mutateAsync: (values: Values) => Promise<T>,
  initialEntity: T,
  entityToValues: (entity: T) => Values = identity,
  {
    onSuccess,
    onError,
    onSubmit,
    noSuccessToast,
    name,
    noSetValues,
  }: Options<Values, T> = {},
): [Values, SubmitCb<Values>] {
  const toast = useToast();
  const { t } = useTranslation();
  const track = useTrack();

  const initialValues = useMemo(
    () => entityToValues(initialEntity),
    [initialEntity, entityToValues],
  );

  const submit: SubmitCb<Values> = useCallback(
    async (values, helpers) => {
      track(name ? `${name} Submitted` : 'Submitted');
      try {
        if (onSubmit) {
          onSubmit(values, helpers);
        }
        const res = await mutateAsync(values);
        if (!noSuccessToast) {
          toast.saved();
        }
        if (onSuccess) {
          await onSuccess(res, helpers, values);
        }

        if (noSetValues) return;

        return helpers.setValues(entityToValues(res));
      } catch (e) {
        helpers.setStatus(e);
        if (onError && e instanceof Error) {
          const hideToast = await onError(e, helpers, values);
          if (hideToast) {
            return;
          }
        }
        toast.exception(e);
      }
      helpers.setSubmitting(false);
    },
    [
      mutateAsync,
      onSuccess,
      onError,
      toast,
      t,
      entityToValues,
      noSuccessToast,
      track,
      name,
      onSubmit,
    ], // eslint-disable-line react-hooks/exhaustive-deps
  );
  return [initialValues, submit];
}
